import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Users, Plus, Edit3, X, Save, Trash2, Check, AlertCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Field, Input, Select, Textarea, Button, Spinner, EmptyState, Pill,
} from "@/components/ui";

// =============================================================================
// CrewAdminScreen — /crews   (T-125)
// Manager+ only. CRUD for crews + their member rosters.
// =============================================================================

const CREW_KINDS = [
  { value: "rigging",        label: "Rigging" },
  { value: "fibre",          label: "Fibre" },
  { value: "civil",          label: "Civil" },
  { value: "integration",    label: "Integration" },
  { value: "mixed",          label: "Mixed" },
  { value: "subcontractor",  label: "Subcontractor" },
] as const;

// Crew roles — function within the crew. Independent of the employee's app
// permission tier (users.role). See migration 077 + T-126 design doc.
const CREW_ROLES = [
  { value: "lead_rigger",     label: "Lead rigger" },
  { value: "rigger",          label: "Rigger" },
  { value: "offsider_rigger", label: "Offsider" },
  { value: "tech",            label: "Tech" },
  { value: "driver",          label: "Driver" },
  { value: "apprentice",      label: "Apprentice" },
  { value: "other",           label: "Other" },
] as const;
type CrewRole = typeof CREW_ROLES[number]["value"];

function crewRoleLabel(v: string | null | undefined): string {
  if (!v) return "Tech";
  return CREW_ROLES.find(r => r.value === v)?.label ?? v;
}

// 12-swatch palette — chosen for legibility on the dark calendar background.
// Keep this short so calendar stays readable; if they all clash, swap out a
// hue rather than adding a 13th.
const PALETTE = [
  "#3B82F6", // blue
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#14B8A6", // teal
  "#84CC16", // lime
  "#F97316", // orange
  "#06B6D4", // cyan
  "#A855F7", // purple
  "#64748B", // slate
];

interface Crew {
  crew_id:          string;
  crew_code:        string;
  crew_name:        string;
  crew_kind:        string;
  colour_hex:       string;
  lead_employee_id: string | null;
  active:           boolean;
  notes:            string | null;
}

interface EmployeeOption {
  employee_id: string;
  first_name:  string;
  last_name:   string;
  primary_role_name: string | null;
}

interface CrewMember {
  crew_member_id: string;
  crew_id:        string;
  employee_id:    string;
  member_from:    string;
  member_to:      string | null;
  is_lead:        boolean;
  crew_role:      CrewRole | null;
}

// -----------------------------------------------------------------------------

export default function CrewAdminScreen() {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const [editingId, setEditingId] = useState<string | "new" | null>(null);

  const { data: crews, isLoading, refetch } = useQuery({
    queryKey: ["crews_admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crews")
        .select("*")
        .order("crew_code");
      if (error) throw error;
      return (data ?? []) as Crew[];
    },
  });

  const { data: memberCounts } = useQuery({
    queryKey: ["crews_member_counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_members")
        .select("crew_id")
        .is("member_to", null);
      if (error) throw error;
      const counts = new Map<string, number>();
      for (const row of (data as { crew_id: string }[])) {
        counts.set(row.crew_id, (counts.get(row.crew_id) ?? 0) + 1);
      }
      return counts;
    },
  });

  if (!canManage) {
    return <div className="p-6"><EmptyState icon={Users} title="Manager access required"/></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold inline-flex items-center gap-2">
            <Users size={18} className="text-accent"/> Crews
          </h1>
          <p className="text-[12px] text-text-muted mt-1">
            Define crews, set their colour, manage members. Used by the crew calendar at /schedule.
          </p>
        </div>
        <Button icon={Plus} size="sm" onClick={() => setEditingId("new")}>
          New crew
        </Button>
      </header>

      {isLoading && (
        <div className="flex justify-center py-8"><Spinner size={20}/></div>
      )}

      {!isLoading && (crews ?? []).length === 0 && (
        <EmptyState icon={Users} title="No crews yet"
                    description="Create your first crew to start assigning teams to projects."/>
      )}

      {!isLoading && (crews ?? []).length > 0 && (
        <Card>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-line">
                <th className="text-left py-2 px-3">Code</th>
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Kind</th>
                <th className="text-left py-2 px-3">Colour</th>
                <th className="text-left py-2 px-3">Members</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {crews!.map(c => (
                <tr key={c.crew_id} className="border-b border-line/50 hover:bg-ink-raised/40">
                  <td className="py-2 px-3 font-mono">{c.crew_code}</td>
                  <td className="py-2 px-3">{c.crew_name}</td>
                  <td className="py-2 px-3 capitalize text-text-muted">{c.crew_kind}</td>
                  <td className="py-2 px-3">
                    <span className="inline-block w-4 h-4 rounded-full align-middle" style={{ background: c.colour_hex }}/>
                    <span className="ml-2 text-[11px] font-mono text-text-muted">{c.colour_hex}</span>
                  </td>
                  <td className="py-2 px-3 text-text-muted">{memberCounts?.get(c.crew_id) ?? 0}</td>
                  <td className="py-2 px-3">
                    {c.active
                      ? <Pill label="Active"   tone="ok"/>
                      : <Pill label="Inactive" tone="closed"/>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      type="button"
                      onClick={() => setEditingId(c.crew_id)}
                      className="text-[11px] text-text-muted hover:text-accent inline-flex items-center gap-1"
                    >
                      <Edit3 size={12}/> edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editingId !== null && (
        <CrewDrawer
          crewId={editingId === "new" ? null : editingId}
          onClose={() => { setEditingId(null); refetch(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// CrewDrawer — create/edit modal. Includes member roster editor.
// =============================================================================
interface CurrentMembership {
  crew_member_id: string;
  crew_id:        string;
  crew_code:      string;
  crew_name:      string;
  member_from:    string;
  crew_role:      string;
  is_lead:        boolean;
}

function CrewDrawer({ crewId, onClose }: { crewId: string | null; onClose: () => void }) {
  const isNew = crewId === null;
  const qc = useQueryClient();

  const { data: crew } = useQuery({
    queryKey: ["crew_one", crewId],
    enabled:  !isNew && !!crewId,
    queryFn:  async () => {
      const { data, error } = await supabase.from("crews").select("*").eq("crew_id", crewId!).single();
      if (error) throw error;
      return data as Crew;
    },
  });

  const { data: employees } = useQuery({
    queryKey: ["employees_for_crew_picker"],
    queryFn:  async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name")
        .eq("is_active", true)
        .order("last_name");
      if (error) throw error;
      return (data ?? []) as EmployeeOption[];
    },
  });

  // Where everybody is RIGHT NOW, across every crew. Without this the picker
  // cannot tell an add from a move, and after migration 326 a blind insert for
  // somebody already in a crew is a raw Postgres exclusion-violation in the
  // user's face. James, 21 Aug 2026: "people really are moving all over the shop
  // right now because we are so new... people are just going left right and
  // everywhere." A screen that punishes the common case is the wrong screen.
  const { data: currentCrewOf } = useQuery({
    queryKey: ["crew_membership_now"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("crew_members")
        .select("crew_member_id, crew_id, employee_id, member_from, member_to, crew_role, is_lead, crews:crew_id ( crew_code, crew_name )")
        .lte("member_from", today)
        .or(`member_to.is.null,member_to.gte.${today}`);
      if (error) throw error;
      const m = new Map<string, CurrentMembership>();
      for (const r of (data ?? []) as any[]) {
        m.set(r.employee_id, {
          crew_member_id: r.crew_member_id,
          crew_id:        r.crew_id,
          crew_code:      r.crews?.crew_code ?? "",
          crew_name:      r.crews?.crew_name ?? "",
          member_from:    r.member_from,
          crew_role:      r.crew_role,
          is_lead:        r.is_lead,
        });
      }
      return m;
    },
  });

  const { data: members, refetch: refetchMembers } = useQuery({
    queryKey: ["crew_members_one", crewId],
    enabled:  !isNew && !!crewId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_members")
        .select("*")
        .eq("crew_id", crewId!)
        .is("member_to", null)
        // Lead riggers float to the top, then alphabetical-ish via insertion order.
        .order("is_lead", { ascending: false })
        .order("member_from", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CrewMember[];
    },
  });

  // Form state — initialise from query when crew loads, keep mutable.
  const [code,      setCode]      = useState(crew?.crew_code ?? "");
  const [name,      setName]      = useState(crew?.crew_name ?? "");
  const [kind,      setKind]      = useState(crew?.crew_kind ?? "mixed");
  const [colour,    setColour]    = useState(crew?.colour_hex ?? PALETTE[0]);
  const [leadId,    setLeadId]    = useState(crew?.lead_employee_id ?? "");
  const [active,    setActive]    = useState(crew?.active ?? true);
  const [notes,     setNotes]     = useState(crew?.notes ?? "");
  const [error,     setError]     = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);

  // Sync state when the crew query resolves (only on first load — drawer
  // opened in edit mode but data was still pending).
  useEffect(() => {
    if (crew && !code) {
      setCode(crew.crew_code);
      setName(crew.crew_name);
      setKind(crew.crew_kind);
      setColour(crew.colour_hex);
      setLeadId(crew.lead_employee_id ?? "");
      setActive(crew.active);
      setNotes(crew.notes ?? "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crew]);

  const canSave = code.trim().length > 0 && name.trim().length > 0;

  async function onSave() {
    if (!canSave) return;
    setSaving(true); setError(null);
    try {
      if (isNew) {
        const { data, error } = await supabase.from("crews").insert({
          crew_code: code.trim().toUpperCase(),
          crew_name: name.trim(),
          crew_kind: kind,
          colour_hex: colour,
          lead_employee_id: leadId || null,
          active,
          notes: notes.trim() || null,
        }).select("crew_id").single();
        if (error) throw error;
        // If a lead was picked, also add them as a crew member with is_lead.
        if (leadId && data) {
          await supabase.from("crew_members").insert({
            crew_id: data.crew_id,
            employee_id: leadId,
            is_lead: true,
          });
        }
      } else {
        const { error } = await supabase.from("crews").update({
          crew_code: code.trim().toUpperCase(),
          crew_name: name.trim(),
          crew_kind: kind,
          colour_hex: colour,
          lead_employee_id: leadId || null,
          active,
          notes: notes.trim() || null,
        }).eq("crew_id", crewId);
        if (error) throw error;
      }
      qc.invalidateQueries({ queryKey: ["crews_admin"] });
      qc.invalidateQueries({ queryKey: ["crews_list_for_calendar"] });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  // Everything that changes a roster ends here, so no caller can forget one of
  // the four places crew membership is read from. Before this, the Members count
  // on the table behind the modal and the roster card on the project page both
  // went stale the moment you edited anything - which is a large part of why
  // this screen felt clunky.
  function refreshEverything() {
    refetchMembers();
    qc.invalidateQueries({ queryKey: ["crew_membership_now"] });
    qc.invalidateQueries({ queryKey: ["crews_member_counts"] });
    qc.invalidateQueries({ queryKey: ["crew_roster"] });
    qc.invalidateQueries({ queryKey: ["schedule_crew_members"] });
  }

  // ---------------------------------------------------------------------------
  // Add somebody to this crew, from a date.
  //
  // If they are already in another crew this is a MOVE, and it is written as
  // one: close the old stint the day before, open the new one on the day. Two
  // statements, in that order, because the reverse order would briefly put them
  // in two crews and migration 326 would reject it.
  //
  // The date is the point. "Add to crew" used to insert with no dates at all and
  // let member_from fall through to CURRENT_DATE, so every move was recorded as
  // happening the moment somebody got round to typing it in. On a schedule that
  // colours history by which crew you were in at the time, that is not a
  // cosmetic problem - it is the record being wrong.
  // ---------------------------------------------------------------------------
  async function addMember(employeeId: string, role: CrewRole = "tech", fromDate?: string) {
    if (!crewId) return;
    const from = fromDate || new Date().toISOString().slice(0, 10);
    const existing = currentCrewOf?.get(employeeId);

    if (existing && existing.crew_id === crewId) {
      setError("They are already in this crew.");
      return;
    }

    if (existing) {
      // Leaving day is the day BEFORE they start here, so the two stints touch
      // without overlapping.
      const dayBefore = new Date(new Date(from + "T00:00:00").getTime() - 86_400_000)
        .toISOString().slice(0, 10);

      if (dayBefore < existing.member_from) {
        // They would be leaving before they ever joined - the old stint never
        // covered a real day, so it is a correction, not a move.
        const { error } = await supabase.from("crew_members")
          .delete().eq("crew_member_id", existing.crew_member_id);
        if (error) { setError(error.message); return; }
      } else {
        const { error } = await supabase.from("crew_members")
          .update({ member_to: dayBefore })
          .eq("crew_member_id", existing.crew_member_id);
        if (error) { setError(error.message); return; }
      }
    }

    const { error } = await supabase.from("crew_members").insert({
      crew_id:     crewId,
      employee_id: employeeId,
      member_from: from,
      crew_role:   role,
      is_lead:     role === "lead_rigger",
    });
    if (error) {
      setError(
        error.code === "23P01"
          ? "They are already in another crew over those dates. End that membership first, then add them here."
          : error.code === "23505"
            ? "There is already a membership for this person on this crew starting that day. Pick a different start date."
            : error.message,
      );
      return;
    }
    setError(null);
    refreshEverything();
  }

  async function changeMemberRole(crewMemberId: string, role: CrewRole) {
    const { error } = await supabase
      .from("crew_members")
      .update({ crew_role: role, is_lead: role === "lead_rigger" })
      .eq("crew_member_id", crewMemberId);
    if (error) { setError(error.message); return; }
    setError(null);
    refreshEverything();
  }

  // Which member the user is ending, and on what day. A small inline panel
  // rather than a window.confirm, because the whole point is that the DAY is a
  // decision - and a confirm dialog cannot ask for one.
  const [endingMember, setEndingMember] = useState<{ id: string; name: string } | null>(null);
  const [endDay, setEndDay] = useState(() => new Date().toISOString().slice(0, 10));

  // Soft-end, never delete - the history is what the schedule colours by.
  // Takes a date now: "last day on this crew" rather than always today.
  async function removeMember(crewMemberId: string, lastDay?: string) {
    const day = lastDay || new Date().toISOString().slice(0, 10);
    const { error } = await supabase
      .from("crew_members")
      .update({ member_to: day })
      .eq("crew_member_id", crewMemberId);
    if (error) { setError(error.message); return; }
    setError(null);
    refreshEverything();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50">
      <div className="w-full md:max-w-[760px] md:rounded-lg bg-ink-raised border border-line max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-ink-raised border-b border-line px-4 py-3 flex items-center justify-between">
          <h2 className="text-[15px] font-semibold inline-flex items-center gap-2">
            <Users size={14}/> {isNew ? "New crew" : `Edit ${crew?.crew_code ?? ""}`}
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button icon={Save} size="sm" disabled={!canSave || saving} onClick={onSave}>
              {saving ? <Spinner size={12}/> : "Save"}
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Code" hint="Short, in caps. e.g. CG-RIG-A">
              <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())} mono placeholder="CG-XXX-N"/>
            </Field>
            <Field label="Name">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Riggers — A team"/>
            </Field>
            <Field label="Kind">
              <Select value={kind} onChange={e => setKind(e.target.value)}>
                {CREW_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </Select>
            </Field>
            <Field label="Lead">
              <Select value={leadId} onChange={e => setLeadId(e.target.value)}>
                <option value="">— none —</option>
                {(employees ?? []).map(em => (
                  <option key={em.employee_id} value={em.employee_id}>
                    {em.last_name}, {em.first_name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label="Colour" hint="Drives the calendar bar tint.">
            <div className="flex flex-wrap gap-2">
              {PALETTE.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setColour(p)}
                  className={`w-8 h-8 rounded-full border-2 ${colour === p ? "border-white" : "border-transparent"}`}
                  style={{ background: p }}
                  aria-label={p}
                >
                  {colour === p && <Check size={14} className="text-white mx-auto"/>}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Active">
            <label className="inline-flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}/>
              <span>Crew is active and pickable on assignments</span>
            </label>
          </Field>

          <Field label="Notes">
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}/>
          </Field>

          {error && (
            <div className="text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2 inline-flex items-start gap-2">
              <AlertCircle size={12} className="mt-0.5"/> {error}
            </div>
          )}

          {/* Members — only when editing an existing crew (need crew_id). */}
          {!isNew && (
            <div className="border-t border-line pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-[13px] font-semibold inline-flex items-center gap-2">
                  <Users size={13} className="text-text-muted"/> Members
                </h3>
                <span className="text-[11px] text-text-muted">{(members ?? []).length} active</span>
              </div>

              {(members ?? []).length > 0 ? (
                <div className="space-y-1.5">
                  {members!.map(m => {
                    const em = employees?.find(e => e.employee_id === m.employee_id);
                    return (
                      <div
                        key={m.crew_member_id}
                        className="flex items-center justify-between gap-3 bg-ink border border-line/50 rounded-md px-3 py-2"
                      >
                        <div className="text-[13px] min-w-0 flex-1 truncate">
                          {em ? `${em.last_name}, ${em.first_name}` : m.employee_id}
                          {/* Job title suffix removed — primary_role_name is
                              currently the conflated app permission tier.
                              Once T-126 ships proper job titles we'll surface
                              "· Lead Rigger" etc. here for readability. */}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Select
                            value={m.crew_role ?? "tech"}
                            onChange={e => changeMemberRole(m.crew_member_id, e.target.value as CrewRole)}
                            className="!py-1.5 !text-[12px] !w-[160px]"
                          >
                            {CREW_ROLES.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </Select>
                          <button
                            type="button"
                            onClick={() => setEndingMember({
                              id: m.crew_member_id,
                              name: em ? `${em.first_name} ${em.last_name}` : "this person",
                            })}
                            className="text-text-muted hover:text-lost p-1.5 rounded hover:bg-lost/10 transition-colors"
                            title="End their time on this crew"
                          >
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[12px] text-text-muted italic px-3 py-2 bg-ink/40 rounded-md">
                  No members yet. Add the first one below.
                </div>
              )}

              {endingMember && (
                <div className="border border-warn/40 bg-warn/[0.06] rounded-md p-3 space-y-2">
                  <div className="text-[12px] text-text">
                    Last day on this crew for <strong>{endingMember.name}</strong>
                  </div>
                  <div className="text-[11px] text-text-muted">
                    Their history stays. They keep showing on the schedule up to and
                    including this day, then move to "Not in a crew" until you put
                    them somewhere.
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input type="date" value={endDay}
                      onChange={e => setEndDay(e.target.value)}
                      className="!w-[170px]" />
                    <Button size="sm" variant="secondary"
                      onClick={() => setEndingMember(null)}>Cancel</Button>
                    <Button size="sm"
                      onClick={async () => {
                        await removeMember(endingMember.id, endDay);
                        setEndingMember(null);
                      }}>End membership</Button>
                  </div>
                </div>
              )}

              {/* Add new member — pick employee + role together. Sits in its
                  own card so it doesn't feel crammed against the roster. */}
              <div className="bg-ink-raised/40 border border-dashed border-line rounded-md p-3">
                <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">
                  Add a member
                </div>
                <AddMemberRow
                  employees={employees ?? []}
                  currentCrewOf={currentCrewOf}
                  thisCrewId={crewId}
                  onAdd={addMember}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// AddMemberRow — pick who, from when, and as what.
// -----------------------------------------------------------------------------
// Three things this now does that it did not:
//
//   1. A DATE. "From" defaults to today and can be any day. Membership is dated
//      and the schedule colours history by it, so a move typed in on Friday for
//      a change that happened Monday has to be able to say Monday.
//
//   2. It knows where the person already is, and says so on the option itself,
//      so the picker reads "Buckney, Jairyn - in RIG-01" instead of pretending
//      everybody is free. Ex-members are no longer filtered out either; they
//      were being hidden while still colliding with the unique constraint on
//      re-add, which is how you get a raw 23505 from a button that looked safe.
//
//   3. It calls what it is. Picking somebody already in a crew turns the button
//      into "Move to this crew" and spells out the consequence underneath,
//      because closing one stint and opening another IS the operation and
//      hiding that behind the word "Add" is how the data got tangled in the
//      first place.
// -----------------------------------------------------------------------------
function AddMemberRow({
  employees, currentCrewOf, thisCrewId, onAdd,
}: {
  employees: EmployeeOption[];
  currentCrewOf: Map<string, CurrentMembership> | undefined;
  thisCrewId: string | null;
  onAdd: (employeeId: string, role: CrewRole, fromDate: string) => void;
}) {
  const [emId, setEmId] = useState("");
  const [role, setRole] = useState<CrewRole>("tech");
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));

  const existing = emId ? currentCrewOf?.get(emId) : undefined;
  const isMove   = !!existing && existing.crew_id !== thisCrewId;
  const alreadyHere = !!existing && existing.crew_id === thisCrewId;

  const dayBefore = (() => {
    try {
      return new Date(new Date(from + "T00:00:00").getTime() - 86_400_000)
        .toISOString().slice(0, 10);
    } catch { return from; }
  })();

  function commit() {
    if (!emId || alreadyHere) return;
    onAdd(emId, role, from);
    setEmId("");
    setRole("tech");
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_150px_150px] gap-2">
        <Select value={emId} onChange={e => setEmId(e.target.value)}>
          <option value="">Pick an employee…</option>
          {employees.map(em => {
            const at = currentCrewOf?.get(em.employee_id);
            return (
              <option key={em.employee_id} value={em.employee_id}>
                {em.last_name}, {em.first_name}
                {at ? ` — in ${at.crew_code}` : " — no crew"}
              </option>
            );
          })}
        </Select>

        <Select value={role} onChange={e => setRole(e.target.value as CrewRole)}>
          {CREW_ROLES.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </Select>

        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} />
      </div>

      {alreadyHere && (
        <div className="text-[11px] text-text-muted">They are already in this crew.</div>
      )}

      {isMove && (
        <div className="text-[11px] text-warn flex items-start gap-1.5">
          <AlertCircle size={11} className="flex-shrink-0 mt-0.5"/>
          <span>
            Currently in <strong>{existing!.crew_code}</strong>. Moving them ends that
            on <strong>{dayBefore}</strong> and starts here on <strong>{from}</strong>.
            Their history with {existing!.crew_code} is kept.
          </span>
        </div>
      )}

      <div className="flex justify-end">
        <Button size="sm" disabled={!emId || alreadyHere} onClick={commit}>
          {isMove ? "Move to this crew" : "Add to crew"}
        </Button>
      </div>
    </div>
  );
}
